import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, RefreshCw, ArrowLeft, ShieldCheck, LogOut } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isActive } from "@/App";
import { loadSnap } from "@/lib/midtrans";

const PLANS = {
  monthly: { label: "Bulanan", price: "Rp55.000", per: "/bulan", amount: 55000 },
  yearly: { label: "Tahunan", price: "Rp550.000", per: "/tahun", amount: 550000 },
};

export default function Checkout() {
  const navigate = useNavigate();
  const { user, checkAuth, logout } = useAuth();
  const [params] = useSearchParams();
  const planParam = params.get("plan") || localStorage.getItem("sk_plan") || "yearly";
  const renewMode = params.get("renew") === "1";

  const [plan, setPlan] = useState(PLANS[planParam] ? planParam : "yearly");
  const [creating, setCreating] = useState(false);

  const expired = user?.subscription_status === "expired";
  const active = isActive(user);

  const doLogout = async () => { await logout(); navigate("/"); };
  const switchAccount = async () => { await logout(); window.location.href = `${window.location.origin}/api/auth/google/login`; };

  const pay = async () => {
    setCreating(true);
    try {
      await loadSnap();
      const { data } = await api.post("/payment/create", { plan });
      if (!window.snap || !data.token) throw new Error("snap-unavailable");
      window.snap.pay(data.token, {
        onSuccess: () => navigate("/payment/success"),
        onPending: () => navigate("/payment/success"),
        onError: () => { toast.error("Pembayaran gagal. Coba lagi ya."); setCreating(false); },
        onClose: () => { toast("Pembayaran dibatalkan."); setCreating(false); checkAuth(); },
      });
    } catch (e) {
      toast.error("Gagal memulai pembayaran. Coba lagi ya.");
      setCreating(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-md w-full">
        <button onClick={() => navigate(renewMode || active ? "/dashboard" : "/")} className="flex items-center gap-1.5 text-sm font-semibold text-sk-muted hover:text-sk-cream mb-4">
          <ArrowLeft className="h-4 w-4" /> {renewMode || active ? "Kembali ke dashboard" : "Kembali"}
        </button>

        {/* Account bar — always accessible, even without an active subscription */}
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-sk-line bg-sk-surface px-4 py-3 mb-4" data-testid="account-bar">
          <p className="text-sm text-sk-muted min-w-0 truncate">Kamu login sebagai <span className="font-semibold text-sk-cream" data-testid="account-email">{user?.email || "—"}</span></p>
          <button onClick={doLogout} data-testid="paywall-logout-btn" className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-sk-line px-3 py-1.5 text-xs font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
        <div className="mb-4 -mt-2 text-right">
          <button onClick={switchAccount} data-testid="ganti-akun-btn" className="text-xs font-semibold text-sk-brand hover:underline">Ganti akun</button>
        </div>

        <div className="rounded-2xl border border-sk-line bg-sk-surface p-7 shadow-sm">
          <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">{(expired || renewMode) ? "Perpanjang langganan" : "Buka semua fitur"}</p>
          <h1 className="font-extrabold text-2xl sm:text-3xl tracking-tight text-sk-cream">{renewMode ? "Perpanjang langganan kamu" : (expired ? "Langganan kamu sudah habis" : "Pilih paket kamu")}</h1>
          <p className="text-sk-muted mt-2 text-sm leading-relaxed">{(expired || renewMode) ? "Perpanjang untuk lanjut akses semua tool kreatif Studio Kreator." : "Selesaikan pembayaran untuk mulai pakai semua fitur."}</p>

          <div className="grid grid-cols-2 gap-3 mt-6">
            {Object.entries(PLANS).map(([key, p]) => (
              <button key={key} onClick={() => setPlan(key)} data-testid={`plan-${key}`} className={`rounded-2xl border p-4 text-left transition-colors ${plan === key ? "border-sk-brand bg-sk-brand/10" : "border-sk-line bg-sk-bg hover:border-sk-surface2"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sk-cream text-sm">{p.label}</span>
                  {plan === key && <Check className="h-4 w-4 text-sk-brand" />}
                </div>
                <p className="font-extrabold text-sk-cream mt-2">{p.price}</p>
                <p className="text-xs text-sk-muted">{p.per}{key === "yearly" ? " · hemat 2 bulan" : ""}</p>
              </button>
            ))}
          </div>

          <p className="text-xs text-sk-muted mt-4 leading-relaxed rounded-xl bg-sk-bg border border-sk-line px-4 py-3" data-testid="other-email-helper">
            Sudah berlangganan pakai email lain? <button onClick={switchAccount} data-testid="ganti-akun-link" className="font-semibold text-sk-brand hover:underline">Logout dan masuk</button> dengan email tersebut.
          </p>

          <button onClick={pay} disabled={creating} data-testid="pay-now-btn" className="mt-5 w-full flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold py-3.5 hover:bg-sk-brandDark transition-colors disabled:opacity-60 shadow-lg shadow-sk-brand/20">
            {creating ? <><RefreshCw className="h-4 w-4 animate-spin" /> Memproses...</> : <>Bayar {PLANS[plan].price}</>}
          </button>
          <p className="text-[11px] text-sk-muted mt-4 text-center flex items-center justify-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Pembayaran diproses aman lewat Midtrans</p>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-sk-bg text-sk-cream flex flex-col">
      <header className="border-b border-sk-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center">
          <img src="/logo.png" alt="Studio Kreator" className="h-8 w-auto" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-10">{children}</main>
    </div>
  );
}
